/**
 * Button.
 *
 * Variants (from UX guidelines):
 *   - primary: filled accent, white text — one per screen
 *   - secondary: white bg + border — multiple per screen
 *   - tertiary: text-only — cancel, low-emphasis
 *   - destructive: filled status-danger — cancel-batch, delete, reject
 *
 * Sizes:
 *   - default (h-9): row-level buttons, modal primary
 *   - compact (h-8): toolbar, inline
 *   - form (h-10): form submit
 *
 * Interaction:
 *   - When `loading`, shows a 16px spinner left of the children and disables.
 *   - Renders an `<a>` if `href` is supplied (TanStack Router Link
 *     composition happens in the caller).
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive';
export type ButtonSize = 'compact' | 'default' | 'form';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-hover disabled:opacity-50',
  secondary:
    'bg-surface text-primary border border-border-default hover:bg-subtle disabled:opacity-50 disabled:hover:bg-surface',
  tertiary: 'bg-transparent text-secondary hover:bg-subtle disabled:opacity-50',
  destructive:
    'bg-status-danger text-accent-fg hover:opacity-90 active:opacity-80 disabled:opacity-50',
};

const sizeClasses: Record<ButtonSize, string> = {
  compact: 'h-8 px-3 text-[13px]',
  default: 'h-9 px-4 text-sm',
  form: 'h-10 px-5 text-sm',
};

export function Button({
  variant = 'secondary',
  size = 'default',
  loading = false,
  disabled,
  leftIcon,
  rightIcon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner className="h-4 w-4" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
}
